import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { CellContext, ColumnDef } from '@tanstack/react-table';
import { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import Chip from 'components/Chip/Chip';
import { DATE_TIME_FORMAT_SHORTER } from 'constants/constants';
import {
  PlanStatus,
  PlanResponse,
  BillingPeriodUnit
} from '../../../common/contracts';
import ActionCell from 'components/DataTableComponent/components/ActionCell';

export function usePlansColumns(
  archivePlan: UseMutationResult<PlanResponse, Error, string, unknown>,
  getPlans: UseQueryResult<PlanResponse[], unknown>
) {
  const navigate = useNavigate();

  const columns: ColumnDef<any>[] = [
    { accessorKey: 'externalName', header: 'Name' },
    { accessorKey: 'externalName', header: 'External name' },
    {
      accessorKey: 'updatedAt',
      header: 'Last update',
      cell: (cell: CellContext<any, unknown>) => {
        const updatedAt = cell.getValue() as string;
        return updatedAt
          ? dayjs(updatedAt).utc().format(DATE_TIME_FORMAT_SHORTER)
          : '';
      }
    },
    {
      accessorKey: 'billingPeriodUnit',
      header: 'Billing period',
      cell: (cell: CellContext<any, unknown>) => {
        const billingPeriodUnit = cell.getValue() as BillingPeriodUnit;
        switch (billingPeriodUnit) {
          case BillingPeriodUnit.DAILY:
            return 'Daily';
          case BillingPeriodUnit.WEEKLY:
            return 'Weekly';
          case BillingPeriodUnit.MONTHLY:
            return 'Monthly';
          case BillingPeriodUnit.YEARLY:
            return 'Yearly';
        }
      }
    },
    {
      accessorKey: 'basePriceInUsd',
      header: 'Base price',
      cell: (cell: CellContext<any, unknown>) => {
        const basePriceInUsd = cell.getValue() as number;
        return basePriceInUsd ? `$${basePriceInUsd}` : '';
      }
    },
    { accessorKey: 'billingCycles', header: 'Billing cycles' },
    { accessorKey: 'First payment discount', header: 'First payment discount' },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: (cell: CellContext<any, unknown>) => {
        const status = cell.getValue() as string;
        const chipStyle = status === PlanStatus.ACTIVE ? 'success' : 'danger';
        const capitalizedStatus =
          status.charAt(0).toUpperCase() + status.slice(1);

        return (
          <Chip variant="flat" status={chipStyle}>
            {capitalizedStatus}
          </Chip>
        );
      }
    },
    {
      accessorKey: 'actions',
      header: '',
      maxSize: 5,
      size: 5,
      cell: (cell: CellContext<any, unknown>) => {
        const { original } = cell.row;
        return (
          <ActionCell
            rowData={original}
            actions={[
              {
                label: 'Edit',
                handler: () => {
                  navigate(`./form/${original.planId}`);
                }
              },
              {
                label: 'Archive',
                handler: async () => {
                  await archivePlan.mutateAsync(original.planId, {
                    onSuccess: () => {
                      getPlans.refetch();
                    }
                  });
                }
              }
            ]}
          />
        );
      }
    }
  ];

  return { columns };
}
